import { useState } from 'react';
import { supabase } from '../supabaseClient';

const T = {
  en: {
    title: 'Set a new password',
    subtitle: 'Enter your new password below.',
    newPass: 'New password',
    confirmPass: 'Confirm password',
    passPh: '••••••••',
    save: 'Save new password',
    saving: 'Saving…',
    enter: 'Enter app',
    mismatch: 'Passwords do not match.',
    tooShort: 'Password must be at least 8 characters.',
    missing: 'Please fill in both fields.',
    genericErr: 'Could not update the password. The link may have expired — request a new one.',
    success: 'Password updated.',
    footer: 'Ground Fresh International Inc.',
  },
  es: {
    title: 'Crear nueva contraseña',
    subtitle: 'Ingresa tu nueva contraseña.',
    newPass: 'Nueva contraseña',
    confirmPass: 'Confirmar contraseña',
    passPh: '••••••••',
    save: 'Guardar contraseña',
    saving: 'Guardando…',
    enter: 'Entrar a la app',
    mismatch: 'Las contraseñas no coinciden.',
    tooShort: 'La contraseña debe tener al menos 8 caracteres.',
    missing: 'Llena ambos campos.',
    genericErr: 'No se pudo actualizar la contraseña. El link pudo haber expirado — solicita uno nuevo.',
    success: 'Contraseña actualizada.',
    footer: 'Ground Fresh International Inc.',
  },
};

const NAVY = '#0D1B2A';
const NAVY_2 = '#152A40';
const ACCENT = '#5BBCB0';
const ACCENT_DARK = '#3D9B8F';

function ResetLogo() {
  return (
    <svg width="96" height="96" viewBox="-55 -55 110 110" style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id="resetLogoGl" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.25)" />
          <stop offset="40%" stopColor="rgba(255,255,255,0.05)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.15)" />
        </linearGradient>
      </defs>
      <rect x="-35" y="-35" width="70" height="70" rx="16" fill="#1B3A35" />
      <rect x="-35" y="-35" width="70" height="70" rx="16" fill="url(#resetLogoGl)" />
      <text x="0" y="2" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="22" fontWeight="700" fill={ACCENT} letterSpacing="3">GFI</text>
      <rect x="-16" y="10" width="32" height="1.2" rx="0.6" fill={ACCENT_DARK} />
      <text x="0" y="22" textAnchor="middle" fontFamily="'DM Sans', sans-serif" fontSize="8" fontWeight="500" fill={ACCENT_DARK} letterSpacing="3">NEXUS</text>
    </svg>
  );
}

export default function ResetPasswordPage({ lang, onDone }) {
  const t = T[lang] || T.es;
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!password || !confirm) { setError(t.missing); return; }
    if (password.length < 8) { setError(t.tooShort); return; }
    if (password !== confirm) { setError(t.mismatch); return; }
    setBusy(true);
    try {
      const { error: upErr } = await supabase.auth.updateUser({ password });
      if (upErr) { setError(t.genericErr); setBusy(false); return; }
      setDone(true);
      setBusy(false);
    } catch (err) {
      setError(t.genericErr);
      setBusy(false);
    }
  };

  const inp = {
    width: '100%', padding: '11px 14px', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(255,255,255,0.06)', color: '#fff',
    fontSize: 14, fontFamily: 'inherit', outline: 'none',
    transition: 'border-color 0.15s, background 0.15s',
    boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block', fontSize: 11, fontWeight: 600,
    color: 'rgba(255,255,255,0.75)', marginBottom: 6,
    letterSpacing: '0.04em', textTransform: 'uppercase',
  };

  return (
    <div style={{
      minHeight: '100vh', width: '100%',
      background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_2} 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, position: 'relative', overflow: 'hidden',
    }}>
      <div aria-hidden style={{ position: 'absolute', top: '-20%', right: '-10%', width: 500, height: 500, borderRadius: '50%', background: `radial-gradient(circle, ${ACCENT}22 0%, transparent 70%)`, pointerEvents: 'none' }} />
      <div aria-hidden style={{ position: 'absolute', bottom: '-20%', left: '-10%', width: 500, height: 500, borderRadius: '50%', background: `radial-gradient(circle, ${ACCENT_DARK}22 0%, transparent 70%)`, pointerEvents: 'none' }} />

      <form onSubmit={onSubmit} style={{
        width: '100%', maxWidth: 400, padding: '40px 36px',
        borderRadius: 16, background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.10)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        position: 'relative', zIndex: 1,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 28 }}>
          <ResetLogo />
          <h1 style={{ margin: '18px 0 4px', color: '#fff', fontSize: 24, fontWeight: 700, letterSpacing: '0.02em', fontFamily: "'DM Sans', sans-serif", textAlign: 'center' }}>
            {t.title}
          </h1>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 500, textAlign: 'center', lineHeight: 1.45, maxWidth: 300 }}>
            {t.subtitle}
          </p>
        </div>
        {done ? (
          <>
            <div role="status" style={{ padding: '14px 16px', borderRadius: 8, background: 'rgba(91,188,176,0.12)', border: '1px solid rgba(91,188,176,0.4)', color: ACCENT, fontSize: 13, fontWeight: 600, marginBottom: 18, textAlign: 'center' }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>✓</div>
              {t.success}
            </div>
            <button type="button" onClick={onDone} style={{
              width: '100%', padding: '12px 16px', borderRadius: 8, border: 'none',
              background: ACCENT, color: NAVY, fontSize: 14, fontWeight: 700,
              letterSpacing: '0.03em', cursor: 'pointer', fontFamily: 'inherit',
              boxShadow: '0 4px 14px rgba(91,188,176,0.25)',
            }}>
              {t.enter}
            </button>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="rst-pw" style={labelStyle}>{t.newPass}</label>
              <div style={{ position: 'relative' }}>
                <input
                  id="rst-pw"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.passPh}
                  disabled={busy}
                  style={{ ...inp, paddingRight: 42 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                  style={{
                    position: 'absolute', right: 10, top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent', border: 'none',
                    padding: 4, cursor: 'pointer',
                    color: 'rgba(255,255,255,0.55)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {showPw ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label htmlFor="rst-pw2" style={labelStyle}>{t.confirmPass}</label>
              <div style={{ position: 'relative' }}>
                <input
                  id="rst-pw2"
                  type={showPw2 ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder={t.passPh}
                  disabled={busy}
                  style={{ ...inp, paddingRight: 42 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw2((v) => !v)}
                  aria-label={showPw2 ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                  style={{
                    position: 'absolute', right: 10, top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent', border: 'none',
                    padding: 4, cursor: 'pointer',
                    color: 'rgba(255,255,255,0.55)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {showPw2 ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div role="alert" style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.4)', color: '#FCA5A5', fontSize: 12, fontWeight: 600, marginBottom: 14, textAlign: 'center' }}>
                {error}
              </div>
            )}

            <button type="submit" disabled={busy} style={{
              width: '100%', padding: '12px 16px', borderRadius: 8, border: 'none',
              background: busy ? ACCENT_DARK : ACCENT, color: NAVY,
              fontSize: 14, fontWeight: 700, letterSpacing: '0.03em',
              cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
              boxShadow: '0 4px 14px rgba(91,188,176,0.25)',
            }}>
              {busy ? t.saving : t.save}
            </button>
          </>
        )}

        <div style={{ marginTop: 24, textAlign: 'center', fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {t.footer}
        </div>
      </form>
    </div>
  );
}
